import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router';
import Team from 'records/Team';
import DepartmentWithTeams from 'records/admin/topics/DepartmentWithTeams';
import TeamListItem from 'components/admin/topics/TeamListItem';
import ToggleArrow from 'components/admin/topics/ToggleArrow';
import './DepartmentListItem.styl';
import * as immutable from 'immutable';
import NewFilterWordProfilesInAccount from 'records/admin/topics/NewFilterWordProfilesInAccount';
import ReactDOM from 'react-dom';
import scrollIntoView from 'dom-scroll-into-view';
import AdminFilterWordProfilePageActions from 'actions/admin/AdminFilterWordProfilePageActions';
import {ProfileEditorKey} from 'stores/admin/AdminFilterWordProfilePageStore';
import classNames from 'classnames';

/** Id of the placeholder team representing a department's default profile. */
const DEPARTMENT_DEFAULTS_TEAM_ID = ''; // not a valid id of an actual team.

/**
 * Each item represents a department. In its default unexpanded state, it displays summary info
 * about the department. It expands to a list of teams in the department, each with profile
 * settings that can be adjusted.
 */
export default class DepartmentListItem extends React.PureComponent {
  static propTypes = {
    department: PropTypes.instanceOf(DepartmentWithTeams).isRequired,
    editable: PropTypes.bool.isRequired,
    expanded: PropTypes.bool.isRequired,
    expandedTeamIds: PropTypes.instanceOf(immutable.Set).isRequired,
    idToProfileMap: PropTypes.instanceOf(immutable.Map).isRequired,
    initiallyExpanded: PropTypes.bool,
    profileDepartmentAndTeamUsage: PropTypes.instanceOf(immutable.Map).isRequired,
    profileEditorKey: PropTypes.instanceOf(ProfileEditorKey),
    profilesInAccount: PropTypes.instanceOf(NewFilterWordProfilesInAccount).isRequired,
    topicsInAccount: PropTypes.instanceOf(immutable.List).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      scrolled: false,
    };
  }

  toggleExpanded(departmentId) {
    AdminFilterWordProfilePageActions.toggleDepartment(departmentId);
  }

  componentDidMount() {
    const {initiallyExpanded} = this.props;
    const {scrolled} = this.state;
    if (initiallyExpanded && !scrolled) {
      const domNode = ReactDOM.findDOMNode(this);
      const rect = domNode.getBoundingClientRect();
      const shouldScroll = rect.top < 0 || rect.bottom > window.innerHeight;
      if (shouldScroll) {
        window.requestAnimationFrame(() => {
          scrollIntoView(domNode, window, {
            alignWithTop: true,
            allowHorizontalScroll: false,
            offsetTop: 20,
          });
          this.setState({
            scrolled: true,
          });
        });
      }
    }
  }

  render() {
    const {
      department,
      editable,
      expanded,
      expandedTeamIds,
      idToProfileMap,
      profileDepartmentAndTeamUsage,
      profileEditorKey,
      profilesInAccount,
      topicsInAccount,
    } = this.props;
    const {departmentId, filterWordProfileId, name, teams} = department;
    const teamElements =
      expanded &&
      [
        new Team({
          // not a valid id for a real team, so no chance of key collision below, or of
          // multiple "defaults" throwing off switching.
          id: DEPARTMENT_DEFAULTS_TEAM_ID,
          name: 'Default Profile For Department',
          filterWordProfileId,
        }),
        ...teams,
      ].map(team => (
        <TeamListItem
          key={team.id}
          department={department}
          editable={editable}
          expanded={expanded && expandedTeamIds.has(team.id)}
          idToProfileMap={idToProfileMap}
          isDefaultForDepartment={team.id === DEPARTMENT_DEFAULTS_TEAM_ID}
          profileDepartmentAndTeamUsage={profileDepartmentAndTeamUsage}
          profileEditorKey={profileEditorKey}
          profilesInAccount={profilesInAccount}
          team={team}
          topicsInAccount={topicsInAccount}
        />
      ));
    const profile = idToProfileMap.get(filterWordProfileId);
    if (filterWordProfileId && !profile) {
      // Should never happen. Probably means we're not handling loading correctly.
      console.error(`No profile found for ${filterWordProfileId}`);
    }
    const defaultProfileName = profile ? profile.name : 'N/A';
    const numTeamsWithOverrides = teams.filter(team => team.filterWordProfileId).size;
    let overridesMessage = '';
    if (numTeamsWithOverrides !== 0) {
      const hasPhrase = numTeamsWithOverrides === 1 ? ' team has ' : ' teams have ';
      overridesMessage = `${numTeamsWithOverrides} ${hasPhrase} overrides.`;
    }
    return (
      <div className="department-list-item">
        <div
          className={classNames('department-bar', expanded && 'expanded')}
          onClick={() => this.toggleExpanded(departmentId)}>
          <div className="department-name">
            {name}
            {expanded && editable && (
              <div className="department-edit-link">
                <Link
                  className="generic-link"
                  to={`/admin/departments/edit/${departmentId}`}
                  onlyActiveOnIndex={false}>
                  Edit Department
                </Link>
              </div>
            )}
          </div>
          {!expanded && (
            <div className="department-default-profile">
              <b>Default Topic Profile:</b> {defaultProfileName}. {overridesMessage}
            </div>
          )}

          <div className="toggle-arrow-wrapper">
            <ToggleArrow expanded={expanded} />
          </div>
        </div>
        {expanded && (
          <div className="expanded-content">
            <div className="expanded-content-header">
              <div className="team-name">Applies To Team</div>
              <div>Keyword/Topic Profile</div>
            </div>
            <div className="teams">{teamElements}</div>
          </div>
        )}
      </div>
    );
  }
}
